import React from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Desktop: table | Mobile: stacked cards
 */
const ComparisonTable = ({ tfsaRoom, rrspRoom, rrspSaving, fhsaSaving, recommended, residencyYear }) => {
  const { t } = useTranslation();
  const c = 'calc.comparison';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const accounts = [
    {
      name: 'TFSA',
      deductible: t(`${c}.no`),
      withdrawal: t(`${c}.taxFree`),
      flexibility: t(`${c}.high`),
      limit: `$7,000`,
      accumulated: tfsaRoom > 0 ? `$${tfsaRoom.toLocaleString()}` : null,
      accumulatedLabel: residencyYear ? t(`${c}.yourAccumulatedRoom`, { year: residencyYear }) : null,
      saving: '$0',
      savingNote: t(`${c}.allYours`),
      color: '#2E7D32',
    },
    {
      name: 'RRSP',
      deductible: t(`${c}.yes`),
      withdrawal: t(`${c}.taxableWithdrawal`),
      flexibility: t(`${c}.low`),
      limit: rrspRoom > 0 ? `~$${rrspRoom.toLocaleString()}` : rrspRoom === 0 ? '$0' : '—',
      saving: rrspSaving > 0 ? `~$${Math.round(rrspSaving).toLocaleString()}` : '—',
      savingNote: t(`${c}.afterTax`),
      color: '#1565C0',
    },
    {
      name: 'FHSA',
      deductible: t(`${c}.yes`),
      withdrawal: t(`${c}.taxFreeHome`),
      flexibility: t(`${c}.medium`),
      limit: '$8,000',
      saving: `~$${Math.round(fhsaSaving).toLocaleString()}`,
      savingNote: null,
      color: '#E65100',
    },
  ];

  const features = [
    { key: 'deductible', label: t(`${c}.deductible`) },
    { key: 'withdrawal', label: t(`${c}.withdrawal`) },
    { key: 'flexibility', label: t(`${c}.flexibilityLabel`) },
    { key: 'limit', label: t(`${c}.limit2026`) },
    { key: 'saving', label: t(`${c}.taxSavingEst`) },
  ];

  // ── Mobile cards ──
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {accounts.map((acct) => (
          <Box
            key={acct.name}
            sx={{
              border: `1px solid ${recommended === acct.name ? acct.color : '#EEF0F3'}`,
              borderRadius: 3, p: 2.5, bgcolor: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 18, color: acct.color }}>
                {acct.name}
              </Typography>
              {recommended === acct.name && (
                <Chip
                  label={t('calc.results.recommended')}
                  size="small"
                  sx={{ bgcolor: acct.color, color: '#fff', fontWeight: 700, fontSize: 12, height: 22 }}
                />
              )}
            </Box>
            {features.map((f) => (
              <Box key={f.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography sx={{ fontSize: 13, color: '#5F6B7A' }}>{f.label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>
                  {acct[f.key]}
                </Typography>
              </Box>
            ))}
            {acct.accumulated && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography sx={{ fontSize: 13, color: '#5F6B7A' }}>{acct.accumulatedLabel}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#2E7D32' }}>
                  {acct.accumulated}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // ── Desktop table ──
  return (
    <TableContainer sx={{ borderRadius: 3, border: '1px solid #EEF0F3', bgcolor: '#fff' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#FAFBFD' }}>
            <TableCell sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: 13 }}>
              {t(`${c}.feature`)}
            </TableCell>
            {accounts.map((a) => (
              <TableCell key={a.name} align="center" sx={{ fontWeight: 800, color: a.color, fontSize: 15 }}>
                {a.name}
                {recommended === a.name && (
                  <Chip
                    label={t('calc.results.recommended')}
                    size="small"
                    sx={{ ml: 1, bgcolor: a.color, color: '#fff', fontWeight: 700, fontSize: 12, height: 20 }}
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {features.map((f) => (
            <React.Fragment key={f.key}>
              <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ fontSize: 13, color: '#5F6B7A' }}>{f.label}</TableCell>
                {accounts.map((a) => (
                  <TableCell key={a.name} align="center" sx={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {a[f.key]}
                  </TableCell>
                ))}
              </TableRow>
              {f.key === 'limit' && accounts[0].accumulated && (
                <TableRow>
                  <TableCell sx={{ fontSize: 13, color: '#5F6B7A' }}>{accounts[0].accumulatedLabel}</TableCell>
                  {accounts.map((a) => (
                    <TableCell key={a.name} align="center" sx={{ fontSize: 14, fontWeight: 600, color: a.accumulated ? '#2E7D32' : 'inherit' }}>
                      {a.accumulated || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ComparisonTable;
